import {
  collection, deleteDoc, doc, limit, onSnapshot, orderBy, query,
  serverTimestamp, setDoc, addDoc, type Timestamp,
} from "firebase/firestore";
import { db } from "./ambiente";
import { perfilQueVale } from "./users";
import type { GuessDoc, RatingDoc, ReactionDoc } from "./schema";

/* ============================================================
   Nota, palpite e comentário

   Cada um é uma SUBCOLEÇÃO com um documento por pessoa, não um mapa
   dentro do prato. Essa diferença é a correção do vazamento de
   anonimato do app anterior: lá as notas ficavam em
   `plates/{pid}.ratings = { uid: nota }`, e qualquer pessoa que abrisse
   o prato baixava o mapa inteiro — a nota era anônima só na tela, e um
   `console.log` desfazia isso. Aqui a regra do Firestore só deixa cada
   um ler o PRÓPRIO documento de nota; a média vem agregada pelo
   servidor no prato.

   Palpite é legível por todo mundo de propósito: o placar de quem chegou
   mais perto é a graça da brincadeira. O que ele NÃO deixa fazer é
   editar depois de enviado — senão dava para "chutar" olhando a
   resposta.
   ============================================================ */

const plateRef = (cid: string, pid: string) => doc(db, "challenges", cid, "plates", pid);

/* ---------- nota ---------- */

export function watchMyRating(
  cid: string, pid: string, uid: string, cb: (score: number | null) => void,
): () => void {
  return onSnapshot(
    doc(plateRef(cid, pid), "ratings", uid),
    (s) => cb(s.exists() ? (s.data() as RatingDoc).score : null),
    () => cb(null),
  );
}

/** Tocar de novo na mesma nota desfaz — é como o app anterior se comporta. */
export async function rate(
  cid: string, pid: string, uid: string, score: number, atual: number | null,
): Promise<void> {
  const ref = doc(plateRef(cid, pid), "ratings", uid);
  if (atual === score) { await deleteDoc(ref); return; }
  await setDoc(ref, { score, at: serverTimestamp() });
}

/* ---------- palpite ---------- */

export interface Guess extends GuessDoc {
  /** O id do documento, "<uid>_price". Serve de chave de lista. */
  gid: string;
  /** Quem chutou. */
  uid: string;
}

/** "ana_price" -> "ana" */
export function donoDoPalpite(gid: string): string {
  return gid.replace(/_(price|kcal)$/, "");
}

export function watchGuesses(
  cid: string, pid: string, kind: GuessDoc["kind"], cb: (list: Guess[]) => void,
): () => void {
  return onSnapshot(
    collection(plateRef(cid, pid), "guesses"),
    (snap) => cb(
      snap.docs
        // O uid sai do ID, e desembrulhá-lo é trabalho DAQUI: uma tela que
        // recebe `uid` esperando um uid não tem por que saber que o tipo do
        // palpite vem grudado nele.
        .map((d) => ({ gid: d.id, uid: donoDoPalpite(d.id), ...(d.data() as GuessDoc) }))
        .filter((g) => g.kind === kind),
    ),
    () => cb([]),
  );
}

/**
 * Envia um palpite.
 *
 * Um documento por pessoa e por prato, imutável depois de enviado — quem
 * decide se cravou, e concede o ponto, é a função. O `awarded` daqui
 * nasce ausente de propósito: se o cliente pudesse escrevê-lo, o palpite
 * viraria uma forma de se dar pontos.
 *
 * O id do documento é o uid, então "kind" precisa caber junto: um mesmo
 * documento carrega o palpite de preço OU o de caloria. Por isso o id
 * leva o tipo no sufixo.
 */
export async function guess(
  cid: string, pid: string, uid: string, kind: GuessDoc["kind"], value: number,
): Promise<void> {
  await setDoc(doc(plateRef(cid, pid), "guesses", `${uid}_${kind}`), {
    kind, value, at: serverTimestamp(),
  });
}

/* ---------- reação ---------- */

/**
 * A reação é pública e tem nome, como o palpite — e ao contrário da nota.
 * Um documento por pessoa é o que faz "uma reação por pessoa" ser fato do
 * banco em vez de convenção de tela.
 */
export function watchMyReaction(
  cid: string, pid: string, uid: string, cb: (emoji: string | null) => void,
): () => void {
  return onSnapshot(
    doc(plateRef(cid, pid), "reactions", uid),
    (s) => cb(s.exists() ? (s.data() as ReactionDoc).emoji : null),
    () => cb(null),
  );
}

/** Mesmo emoji desfaz; outro substitui. É como a nota já se comporta. */
export async function reagir(
  cid: string, pid: string, uid: string, emoji: string, atual: string | null,
): Promise<void> {
  const ref = doc(plateRef(cid, pid), "reactions", uid);
  if (atual === emoji) { await deleteDoc(ref); return; }
  await setDoc(ref, { emoji, at: serverTimestamp() });
}

/* ---------- comentário ---------- */

export interface CommentDoc {
  uid: string;
  name: string;
  photo: string;
  text: string;
  at: Timestamp;
}
export interface Comment extends CommentDoc { id: string }

export function watchComments(
  cid: string, pid: string, cb: (list: Comment[]) => void,
): () => void {
  const q = query(collection(plateRef(cid, pid), "comments"), orderBy("at", "asc"), limit(200));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as CommentDoc) }))),
    () => cb([]),
  );
}

export async function comment(
  cid: string, pid: string,
  autor: { uid: string; name: string; photo: string },
  text: string,
): Promise<void> {
  /* O `autor` que a tela manda é o do login; o que vale é o de
     `users/{uid}` — senão o comentário sai com o nome e a foto do
     Google depois de a pessoa ter trocado os dois em Conta. */
  const quem = await perfilQueVale(autor);
  await addDoc(collection(plateRef(cid, pid), "comments"), {
    ...quem, text, at: serverTimestamp(),
  });
}
